import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import agencyOrdersService from "../../../../../api/agencyOrders/AgencyOrdersService";
import { getNoaId } from "../../../../../utils/session.utils";
import { dateFormats } from "../../../../../helpers/convertDate";
import s from "./PendingOrdersList.module.css";

// ========================== | LISTADO DE PEDIDOS PENDIENTES | ======================================

export const PendingOrdersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const dates = dateFormats();

  // Section: Functions
  const getData = () => {
    const organizationId = getNoaId();
    if (!organizationId) {
      console.error("No se encontró organización");
      return;
    }

    setLoading(true);
    agencyOrdersService
      .getPendingOrders(organizationId, page, pageSize)
      .then((data) => {
        setOrders(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setError(false);
      })
      .catch((error) => {
        console.error("Error al obtener pedidos pendientes:", error);
        setError(true);
        setOrders([]);
        // Si es un 404, el endpoint probablemente no está implementado en el backend
        if (error.response?.status === 404) {
          console.warn("El endpoint de pedidos pendientes no está disponible. Verifique que el backend tenga implementado: GET /delivery-service/agency-orders/pending");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Section: Functions - Obtener estadísticas
  const getStatistics = () => {
    const organizationId = getNoaId();
    if (!organizationId) return;

    setLoadingStats(true);
    agencyOrdersService
      .getStatistics(organizationId)
      .then((data) => {
        setStatistics(data);
      })
      .catch((error) => {
        console.error("Error al obtener estadísticas:", error);
      })
      .finally(() => {
        setLoadingStats(false);
      });
  };

  // Section: Effects
  useEffect(() => {
    getData();
    getStatistics();
  }, [page, pageSize]);

  // Section: Handlers
  const handleRefresh = () => {
    getData();
    getStatistics();
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case "Suficiente":
        return "success";
      case "Insuficiente":
        return "warning";
      case "Crítico":
        return "error";
      default:
        return "default";
    }
  };

  const getStockStatusIcon = (status) => {
    switch (status) {
      case "Suficiente":
        return "🟢";
      case "Insuficiente":
        return "🟡";
      case "Crítico":
        return "🔴";
      default:
        return "";
    }
  };

  const handleViewOrder = (orderId) => {
    console.log("👁️ Ver detalles del pedido ID:", orderId);
    console.log("📍 Navegando a: /main/stock-de-cupones/revisar/" + orderId);
    // Navegar a la pantalla de revisión del pedido
    navigate(`/main/stock-de-cupones/revisar/${orderId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
      case "Pendiente":
        return "warning";
      case "APPROVED":
      case "Aprobado":
        return "success";
      case "REJECTED":
      case "Rechazado":
        return "error";
      default:
        return "default";
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case "PENDING":
        return "Pendiente";
      case "APPROVED":
        return "Aprobado";
      case "REJECTED":
        return "Rechazado";
      default:
        return status || "Pendiente";
    }
  };

  if (loading && orders.length === 0) {
    return (
      <Grid container className={s.container}>
        <Grid item xs={12} className={s.loader}>
          <CircularProgress />
          <p style={{ marginTop: "1rem" }}>Cargando pedidos pendientes...</p>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container className={s.container}>
      <Grid item xs={12} className={s.header}>
        <h3>Pedidos Pendientes de Aprobación</h3>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || loadingStats}
        >
          Actualizar
        </Button>
      </Grid>

      {/* Análisis de Stock por Juego */}
      {statistics && statistics.stockSummary && statistics.stockSummary.length > 0 && (
        <Grid item xs={12} sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📦 Análisis de Stock por Juego
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Juego
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Sorteo
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Stock Disponible
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Solicitado en Pendientes
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Cobertura
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Estado
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                        Pedidos Pendientes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.stockSummary.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{item.gameName}</TableCell>
                        <TableCell>#{item.raffleNumber}</TableCell>
                        <TableCell>
                          {item.totalStockAvailable?.toLocaleString() || 0}
                          {item.stockRangesCount > 1 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              ({item.stockRangesCount} rangos)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.totalRequestedInPendingOrders?.toLocaleString() || 0}</TableCell>
                        <TableCell>{item.coveragePercentage?.toFixed(1) || 0}%</TableCell>
                        <TableCell>
                          <Chip
                            label={`${getStockStatusIcon(item.stockStatus)} ${item.stockStatus}`}
                            color={getStockStatusColor(item.stockStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.pendingOrdersCount || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {error ? (
        <Grid item xs={12} sx={{ textAlign: "center", mt: 4 }}>
          <p style={{ color: "error.main" }}>
            Error al cargar los pedidos. Verifique que el endpoint esté disponible en el backend.
          </p>
        </Grid>
      ) : orders.length === 0 && !loading ? (
        <Grid item xs={12} sx={{ textAlign: "center", mt: 4 }}>
          <p>No hay pedidos pendientes</p>
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      N° Pedido
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Agencia
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Fecha
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Cupones Solicitados
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Estado
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => {
                    // El ID puede venir como orderId o id
                    const orderId = order.orderId || order.id;
                    // Calcular el total solicitado sumando los quantityRequested de los details
                    const totalRequested = order.totalRequested 
                      || order.totalCuponsRequested 
                      || (order.details && order.details.length > 0 
                        ? order.details.reduce((sum, detail) => sum + (detail.quantityRequested || 0), 0)
                        : 0);
                    
                    return (
                      <TableRow key={orderId} hover>
                        <TableCell>#{order.nroOrder || orderId}</TableCell>
                        <TableCell>
                          {order.agencyNumber || order.agency?.number || "-"} - {order.agencyName || order.agency?.name || "-"}
                        </TableCell>
                        <TableCell>{dates.formatDateTimeArgentina(order.requestDate)}</TableCell>
                        <TableCell>{totalRequested.toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={translateStatus(order.status)}
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => handleViewOrder(orderId)}
                            title="Ver detalles"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2, textAlign: "center" }}>
            <p>
              Mostrando {orders.length} de {totalElements} resultados
            </p>
          </Grid>
        </>
      )}
    </Grid>
  );
};

